import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const FeaturesWorkflow = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const steps = useMemo(
    () => [
      {
        number: "01",
        title: tt("featuresWorkflow.steps.start.title", "Start with a topic"),
        text: tt(
          "featuresWorkflow.steps.start.text",
          "Drop in your raw content idea, angle, offer, or rough concept without needing it to be perfect."
        ),
      },
      {
        number: "02",
        title: tt(
          "featuresWorkflow.steps.generate.title",
          "Generate stronger options"
        ),
        text: tt(
          "featuresWorkflow.steps.generate.text",
          "Use Hookora to explore hooks, hook angles, titles, captions, scripts, rewrites, and more from that same core idea."
        ),
      },
      {
        number: "03",
        title: tt(
          "featuresWorkflow.steps.refine.title",
          "Refine what stands out"
        ),
        text: tt(
          "featuresWorkflow.steps.refine.text",
          "Improve retention, voice, positioning, and differentiation so your content feels sharper before you publish."
        ),
      },
      {
        number: "04",
        title: tt(
          "featuresWorkflow.steps.save.title",
          "Save and build faster next time"
        ),
        text: tt(
          "featuresWorkflow.steps.save.text",
          "Keep useful outputs in your library so you can reuse, refine, and create with more consistency."
        ),
      },
    ],
    [t]
  );

  return (
    <section style={{ padding: "12px 20px 84px", background: colors.sectionBg }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <div
          style={{
            background: colors.darkCtaBg,
            borderRadius: "30px",
            padding: "34px 26px",
            color: "#ffffff",
            boxShadow: colors.shadow,
          }}
        >
          <div style={{ maxWidth: "760px", marginBottom: "26px" }}>
            <div
              style={{
                display: "inline-flex",
                padding: "7px 12px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.12)",
                color: "#e0e7ff",
                fontWeight: "700",
                fontSize: "12px",
                marginBottom: "14px",
              }}
            >
              {tt("featuresWorkflow.badge", "How the workflow feels")}
            </div>

            <h2
              style={{
                margin: "0 0 12px",
                fontSize: "clamp(2rem, 4vw, 3rem)",
                lineHeight: "1.08",
              }}
            >
              {tt(
                "featuresWorkflow.title",
                "Built to reduce friction and keep your content momentum alive"
              )}
            </h2>

            <p
              style={{
                margin: 0,
                color: colors.darkCtaMuted,
                lineHeight: "1.75",
                fontSize: "16px",
              }}
            >
              {tt(
                "featuresWorkflow.description",
                "Hookora is most useful when it makes content creation feel easier, clearer, and more repeatable. This is the workflow that gives it that feeling."
              )}
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "14px",
            }}
          >
            {steps.map((step) => (
              <div
                key={step.number}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: "20px",
                  padding: "18px",
                }}
              >
                <div
                  style={{
                    color: "#c4b5fd",
                    fontWeight: "800",
                    fontSize: "13px",
                    letterSpacing: "0.08em",
                    marginBottom: "10px",
                  }}
                >
                  {step.number}
                </div>

                <h3 style={{ margin: "0 0 10px", fontSize: "19px", lineHeight: "1.3" }}>
                  {step.title}
                </h3>

                <p
                  style={{
                    margin: 0,
                    color: "rgba(255,255,255,0.82)",
                    lineHeight: "1.7",
                    fontSize: "14px",
                  }}
                >
                  {step.text}
                </p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: "22px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Link
              to="/register"
              style={{
                textDecoration: "none",
                background: "#ffffff",
                color: "#111827",
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
              }}
            >
              {tt("about.createFreeAccount", "Create Free Account")}
            </Link>

            <Link
              to="/pricing"
              style={{
                textDecoration: "none",
                background: "transparent",
                color: "#ffffff",
                padding: "14px 18px",
                borderRadius: "12px",
                fontWeight: "800",
                border: "1px solid rgba(255,255,255,0.24)",
              }}
            >
              {tt("featuresWorkflow.seePlans", "See Plans")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesWorkflow;